import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { DataHubCredentials, DataHubPublisher } from '../List/DataModels';
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { PagedResponse } from "@iconics/core";
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import { ForwarderTypeEnum, ForwardEventEnum } from '@iconics/providers.fadx-platform-api-client-v2';
import { SerendieSymbol } from '@serendie/symbols';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { MultilingualTextField } from "@iconics/components";
import { Validator } from '@iconics/core';
import { Entity, PublisherEntity, Package } from './DataModels';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextField } from '@iconics/components';

class EditDataHubPublisher extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private originalEntity?: IEntity;
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        const localStorageProvider = appContext().localStorageProvider;

        this.state =
        {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            dataHubCredentialError: '',
            selectedDataHubCredentials: [],
            dataHubCredentials: [],
            moduleConfigurationId: '',
            selectedPublishingTypes: [],
            publishingTypes: [
                { value: 'EventHub', label: this.labels.Platform_DataHubPublisher_PublishingType_EventHub },
                { value: 'EventGrid', label: this.labels.Platform_DataHubPublisher_PublishingType_EventGrid },
                { value: 'AzureFunction', label: this.labels.Platform_DataHubPublisher_PublishingType_AzureFunction },
            ],
            dataPackageError: '',
            selectedDataPackages: [],
            dataPackages: [],
            entityError: '',
            selectedEntities: [],
            entities: [],
            selectedEntityPublishingTypes: [],
            entityPublishingTypeError: '',
            entityPublishingTypes: [
                { value: 'None', label: this.labels.Platform_DataEntity_PublishingType_None },
                { value: 'Whole', label: this.labels.Platform_DataEntity_PublishingType_Whole },
            ],
            concurrencyToken: '',
            canDelete: localStorageProvider.hasRight('DATAMODEL.Tenancy.Delete'), // TODO proper premission does not exist yet
            sections: [],
        };
    }

    private get entityId(): number
    {
        return Number.parseInt(this.state.selectedEntities[0]);
    }

    private get id(): number
    {
        return Number.parseInt(this.props.params.id ?? '0');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_DataHubPublisher_PageTitle;
        this.backUrl = '/admin/data-hub-publishers';
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('dataHubCredential', () => this.state.selectedDataHubCredentials[0] ?? '', i => this.setState({ dataHubCredentialError: i }))
            .isRequired();
        this.validator
            .addInput('dataPackage', () => this.state.selectedDataPackages[0] ?? '', i => this.setState({ dataPackageError: i }))
            .isRequired();
        this.validator
            .addInput('entity', () => this.state.selectedEntities[0] ?? '', i => this.setState({ entityError: i }))
            .isRequired();
        this.validator
            .addInput('entityPublishingType', () => this.state.selectedEntityPublishingTypes[0] ?? '', i => this.setState({ entityPublishingTypeError: i }))
            .isRequired();

        await this.loading(async () =>
        {
            await this.loadDataHubCredentials();
            await this.loadDataPackages();
            await this.loadDataHubPublisher();
        });
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, _: Readonly<IState>): Promise<void>
    {
        if (!prevProps.params.id && this.props.params.id)
        {
            await this.loading(async () =>
            {
                await this.loadDataHubPublisher();
            });
        }
    }

    private async loadDataHubPublisher(): Promise<void>
    {
        if (this.props.params.id === undefined)
        {
            return;
        }
        const dataHubPublisherData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.get<DataHubPublisher>({
            id: this.props.params.id,
            tenancy: appContext().tenantId,
            select: DataHubPublisher,
        });

        // query the attached entities, which should only be one (see #22967)
        const publisherEntitiesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.entities.get<PagedResponse<PublisherEntity[]>>({
            id: this.id,
            tenancy: appContext().tenantId,
            select: PublisherEntity,
        });

        let publisherEntity: PublisherEntity | undefined = undefined;
        let entity: Entity | undefined = undefined;

        if (publisherEntitiesData.value.length > 0)
        {
            publisherEntity = publisherEntitiesData.value[0];
            this.originalEntity = {
                id: publisherEntity.EventTypeId,
                name: publisherEntity.Name,
                etag: publisherEntity['@odata.etag']
            }
            // query the entity to retrieve its package id
            entity = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.get<Entity>({
                id: this.originalEntity.id.toString(),
                select: Entity,
                tenancy: appContext().tenantId,
            });

            // load the entities related the attached entity package, otherwise dropdown would be empty
            await this.handleDataPackageChange(entity.Package_ID);
        }

        await this.setStateAsync({
            name: dataHubPublisherData.Name,
            displayName: this.languageValuesHelper.fromString(dataHubPublisherData.DisplayName),
            selectedPublishingTypes: [dataHubPublisherData.ForwarderType ?? ''],
            selectedDataHubCredentials: [dataHubPublisherData.ForwarderEventHubID.toString() ?? ''],
            moduleConfigurationId: dataHubPublisherData.ModuleConfigurationEntityId,
            concurrencyToken: dataHubPublisherData["@odata.etag"],
            selectedEntities: [entity?.Id.toString() ?? ''],
            selectedEntityPublishingTypes: [publisherEntity?.ForwardEvent ?? '']
        });
    }

    private async loadDataHubCredentials(): Promise<void>
    {
        const dataHubCredentialsData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.get<PagedResponse<DataHubCredentials[]>>({
            select: DataHubCredentials,
            tenancy: appContext().tenantId,
            top: ApiConstants.largeODataTop,
            orderBy: 'Name asc'
        });
        this.setState({
            dataHubCredentials: dataHubCredentialsData.value.map(x => ({
                value: x.Id.toString(),
                label: x.Name,
            })),
        });
    }

    private async loadDataPackages(): Promise<void>
    {
        const dataPackagesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.get<PagedResponse<Package[]>>({
            select: Package,
            tenancy: appContext().tenantId,
            top: ApiConstants.largeODataTop,
            orderBy: 'Name asc'
        });
        this.setState({
            dataPackages: dataPackagesData.value.map(x => ({
                value: x.Id.toString(),
                label: x.Name,
            })),
        });
    }

    private async loadDataEntities(packageId: string): Promise<void>
    {
        const entitiesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.get<PagedResponse<Entity[]>>({
            select: Entity,
            tenancy: appContext().tenantId,
            top: ApiConstants.largeODataTop,
            filter: `Package_ID eq '${packageId}'`,
            orderBy: 'Name asc'
        });
        this.setState({
            entities: entitiesData.value.map(x => ({
                value: x.Id.toString(),
                label: x.Name,
            })),
        });
    }

    private async handleDataPackageChange(value: string): Promise<void>
    {
        this.setState({
            selectedDataPackages: [value],
            selectedEntities: [],
            selectedEntityPublishingTypes: [],
        }, () => this.validator.validate('dataPackage'));
        await this.loading(async () =>
        {
            await this.loadDataEntities(value);
        });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (!this.props.params.id)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.delete({
                tenancy: appContext().tenantId,
                id: this.props.params.id
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        if (this.props.params.id)
        {
            await this.updateDataHubPublisher();
        }
        else
        {
            await this.createDataHubPublisher();
        }
    }

    private async updateDataHubPublisher(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.put<DataHubPublisher>({
                id: this.id,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    DataHubCredential_Id: Number.parseInt(this.state.selectedDataHubCredentials[0]),
                    ModuleConfiguration_Id: this.state.moduleConfigurationId,
                    PublishingType: this.state.selectedPublishingTypes[0] as ForwarderTypeEnum,
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            if (!this.originalEntity)
            {
                await this.createAttachedEntity(this.id);
            }
            else if (this.originalEntity.id === this.entityId) 
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.entities.byEntityId.patch({
                    entityId: this.entityId,
                    id: this.id,
                    tenancy: appContext().tenantId,
                    body: {
                        DataPublisher_Id: this.id,
                        ForwardType: this.state.selectedEntityPublishingTypes[0] as ForwardEventEnum,
                        '@odata.etag': this.originalEntity.etag
                    }
                });
            }
            else if (this.originalEntity.id !== this.entityId)
            {
                // delete old linkage table
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.entities.byEntityId.delete({
                    entityId: this.originalEntity.id,
                    id: this.id,
                    tenancy: appContext().tenantId,
                });

                await this.createAttachedEntity(this.id);
            }
            this.props.navigate(this.backUrl);
        });
    }

    private async createDataHubPublisher(): Promise<void>
    {
        await this.loading(async () =>
        {
            const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.post<DataHubPublisher>({
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    DataHubCredential_Id: Number.parseInt(this.state.selectedDataHubCredentials[0]),
                    ModuleConfiguration_Id: this.state.moduleConfigurationId,
                    PublishingType: this.state.selectedPublishingTypes[0] as ForwarderTypeEnum,
                }
            });

            await this.createAttachedEntity(response.Id);
            this.props.navigate(this.backUrl);
        });
    }
    private async createAttachedEntity(publisherId: number): Promise<void>
    {
        if (this.entityId)
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.byId.entities.byEntityId.post<Entity>({
                id: this.id,
                entityId: this.entityId,
                tenancy: appContext().tenantId,
                body: {
                    Name: `${publisherId}_${this.entityId}`, // the name of this entity<->publisher table needs to be unique but is not shown anywhere
                    DataPublisher_Id: publisherId,
                    ForwardType: this.state.selectedEntityPublishingTypes[0] as ForwardEventEnum,
                }
            });
        }
        else
        {
            console.error('Selected Entity is null.')
        }
    }

    private renderCommonDetails(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_DataHubPublisher_DetailsTitle}
                subTitle={this.labels.Platform_DataHubPublisher_DetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                icon={<SerendieSymbol name='arrow-left-circle' />}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            required
                            label={this.labels.DataHubPublisher_Name}
                            placeholder={this.labels.DataHubPublisher_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                            invalid={!!this.state.nameError}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <MultilingualTextField
                            label={this.labels.DataHubPublisher_DisplayName}
                            placeholder={this.labels.DataHubPublisher_DisplayName}
                            value={this.state.displayName}
                            onChange={value => this.setState({ displayName: value })}
                            localisation={appContext().localisation}
                        />
                    </GridItem>

                    <GridItem expanded={6}>
                        <Select
                            required
                            label={this.labels.DataHubPublisher_DataHubCredential_Id}
                            placeholder={this.labels.DataHubPublisher_DataHubCredential_Id}
                            value={this.state.selectedDataHubCredentials}
                            items={this.state.dataHubCredentials}
                            onSelect={e => this.setState({ selectedDataHubCredentials: [e.value] }, () => this.validator.validate('dataHubCredential'))}
                            invalid={!!this.state.dataHubCredentialError}
                            invalidMessage={this.state.dataHubCredentialError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.DataHubPublisher_ModuleConfiguration_Id}
                            placeholder={this.labels.DataHubPublisher_ModuleConfiguration_Id}
                            value={this.state.moduleConfigurationId}
                            onChange={e => this.setState({ moduleConfigurationId: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={this.labels.DataHubPublisher_PublishingType}
                            placeholder={this.labels.DataHubPublisher_PublishingType}
                            value={this.state.selectedPublishingTypes}
                            items={this.state.publishingTypes}
                            onSelect={e => this.setState({ selectedPublishingTypes: [e.value as ForwarderTypeEnum] })}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private renderDataEntitySection(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_DataHubPublisher_DataEntitySelectionTitle}
                subTitle={this.labels.Platform_DataHubPublisher_DataEntitySelectionSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <Select
                            required
                            label={this.labels.DataHubPublisher_DataPackage}
                            placeholder={this.labels.DataHubPublisher_DataPackage}
                            value={this.state.selectedDataPackages}
                            items={this.state.dataPackages}
                            onSelect={e => this.handleDataPackageChange(e.value)}
                            invalid={!!this.state.dataPackageError}
                            invalidMessage={this.state.dataPackageError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            required
                            label={this.labels.DataHubPublisher_Entity}
                            placeholder={this.labels.DataHubPublisher_Entity}
                            value={this.state.selectedEntities}
                            items={this.state.entities}
                            onSelect={e => this.setState({ selectedEntities: [e.value] }, () => this.validator.validate('entity'))}
                            invalid={!!this.state.entityError}
                            invalidMessage={this.state.entityError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            required
                            label={this.labels.DataHubPublisher_Entity_PublishingType}
                            placeholder={this.labels.DataHubPublisher_Entity_PublishingType}
                            value={this.state.selectedEntityPublishingTypes}
                            items={this.state.entityPublishingTypes}
                            onSelect={e => this.setState({ selectedEntityPublishingTypes: [e.value] }, () => this.validator.validate('entityPublishingType'))}
                            invalid={!!this.state.entityPublishingTypeError}
                            invalidMessage={this.state.entityPublishingTypeError}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer>
                        <GridItem>
                            {this.renderCommonDetails()}
                        </GridItem>
                        <GridItem>
                            {this.renderDataEntitySection()}
                        </GridItem>
                    </GridContainer>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                this.props.params.id &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_DataHubPublisher_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(this.backUrl)}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }


}

export default withRouting(EditDataHubPublisher);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    dataHubCredentialError: string;
    selectedDataHubCredentials: string[];
    dataHubCredentials: { value: string, label: string }[];
    moduleConfigurationId: string;
    selectedPublishingTypes: string[];
    publishingTypes: { value: ForwarderTypeEnum, label: string }[];
    dataPackageError: string;
    selectedDataPackages: string[];
    dataPackages: { value: string, label: string }[];
    entityError: string;
    selectedEntities: string[];
    entities: { value: string, label: string }[];
    entityPublishingTypeError: string;
    selectedEntityPublishingTypes: string[];
    entityPublishingTypes: { value: string, label: string }[];
    concurrencyToken: string;
    canDelete: boolean;
    sections: IPageNavSection[];
}

export interface IEntity
{
    name: string;
    id: number;
    etag: string;
}